import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { 
  LogOut, 
  Settings, 
  CreditCard,
  Zap,
  User,
  ChevronRight,
  Coins,
  RefreshCw,
  Moon,
} from "lucide-react";

import { useTheme } from "next-themes";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { ProfileSwitcher } from "@/components/admin/ProfileSwitcher";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { HEADER_TITLES, CORE_ROUTES } from "@/config/coreRoutes";
import { brainTitle } from "@/lib/brainExport";
import { useBrand } from "@/contexts/BrandContext";
import { useDisplayName } from "@/hooks/useDisplayName";

export function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { credits, getEffectiveLimit, getRemainingCredits, loading: creditsLoading } = useCredits();
  const { isImpersonating, impersonatedUserId } = useImpersonation();
  // resolvedTheme rather than theme: `theme` is the stored preference and can be "system",
  // which is not a thing you can render a checkbox from.
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // VA-222: the breadcrumb on /brand-guide carries the brand's name.
  const { activeBrand } = useBrand();
  const effectiveUserId = isImpersonating && impersonatedUserId ? impersonatedUserId : user?.id;
  const isFreeUser = !creditsLoading && credits !== null && credits.subscription_tier === "basic" && getRemainingCredits() <= 0;
  const [profile, setProfile] = useState<{ display_name: string | null; avatar_url: string | null } | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { isInstalled } = usePWAInstall();

  const handleHardRefresh = async () => {
    setRefreshing(true);
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      window.location.replace(window.location.href);
    } catch {
      window.location.replace(window.location.href);
    } finally {
      setTimeout(() => setRefreshing(false), 3000);
    }
  };

  // Calculate credit usage
  const planLimit = getEffectiveLimit();
  const available = getRemainingCredits();
  const bonusCredits = credits?.bonus_credits ?? 0;
  const monthlyUsed = credits?.monthly_credits_used ?? 0;
  const planRemaining = Math.max(0, planLimit - monthlyUsed);

  // Bar is 100% while bonus credits exist (monthly untouched).
  // Once bonus is 0, bar shows monthly plan remaining.
  const planFillPercent = creditsLoading || planLimit === 0 ? 100
    : bonusCredits > 0 ? 100
    : (planRemaining / planLimit) * 100;
  
  const getProgressColor = () => {
    if (bonusCredits > 0) return "bg-success"; // Still have bonus = fully green
    if (planFillPercent > 50) return "bg-success";
    if (planFillPercent > 20) return "bg-brand-deep-purple";
    return "bg-destructive";
  };

  // Low credit warning
  const isLowCredits = available <= 5 && available > 0;
  const isOutOfCredits = available === 0 && !creditsLoading;

  // Fetch user profile (respects impersonation for View As)
  useEffect(() => {
    const fetchProfile = async () => {
      if (!effectiveUserId) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("user_id", effectiveUserId)
        .single();
      
      if (data) {
        setProfile(data);
      }
    };

    fetchProfile();
  }, [effectiveUserId]);

  // Get page title
  //
  // VA-222: /brand-guide is "The Brain" everywhere in the app now, so the breadcrumb
  // carries the brand name — "My Vision Assistant Brain". HEADER_TITLES is a static
  // map keyed by path and cannot know the active brand, so the override happens here
  // rather than by changing the route label, which is still used for navigation.
  const staticTitle = HEADER_TITLES[location.pathname] || "Dashboard";
  const pageTitle = location.pathname === CORE_ROUTES.brandGuide.path
    ? brainTitle(activeBrand?.business_name)
    : staticTitle;
  
  // Generate initials from display name
  const getInitials = () => {
    if (profile?.display_name) {
      const names = profile.display_name.trim().split(/\s+/);
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].slice(0, 2).toUpperCase();
    }
    return user?.email?.slice(0, 2).toUpperCase() || "U";
  };

  // The shared hook, so the Assistant's greeting and this menu can never disagree about a name.
  const displayName = useDisplayName();

  // The build this browser is running, as a date somebody can read out loud in a ticket.
  // VITE_BUILD_TIMESTAMP is a Date.now() string stamped at build time (vite.config.ts).
  // "dev" when running locally, where there is no build to be stale.
  const buildStamp = (() => {
    const raw = (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_BUILD_TIMESTAMP;
    if (!raw || raw === "dev") return "dev";
    const ms = Number(raw);
    if (!Number.isFinite(ms)) return String(raw);
    // Deliberately not toLocaleString: QA and I are in different timezones, and a version
    // that reads differently on two screens is worse than no version at all.
    return new Date(ms).toISOString().slice(0, 16).replace("T", " ") + " UTC";
  })();

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  return (
    <TooltipProvider delayDuration={0}>
      <header className="sticky top-0 z-30 bg-card border-b border-border">
          <div className="flex h-16 items-center justify-between px-3 sm:px-6 gap-2">
          {/* Left: Page Title with breadcrumb */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 pl-12 lg:pl-0">
            {location.pathname !== "/dashboard" && (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:inline"
                >
                  Dashboard
                </Link>
                <ChevronRight className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </>
            )}
            {/* min-w-0 so a long brand name truncates instead of pushing the row wider (VA-222). */}
            <h1 className="text-base sm:text-xl font-semibold text-foreground truncate min-w-0" title={pageTitle}>{pageTitle}</h1>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {/* AI Credits Widget - hidden for free users who have 0 credits */}
            {isFreeUser ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/plan")}
                className="border-primary text-primary hover:bg-primary/10 gap-1.5"
              >
                <Zap className="h-4 w-4" />
                <span className="hidden sm:inline">Upgrade Plan</span>
                <span className="sm:hidden">Upgrade</span>
              </Button>
            ) : (
              <>
                <Link
                  to="/plan"
                  data-tour="credit-counter"
                  className={cn(
                    // VA-273, Kate 3 Sep: in dark mode the pill's border (20% grey on a 10% header)
                    // could not be seen, so the counter floated with no edge.
                    "relative flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 border hover:bg-muted transition-colors cursor-pointer dark:bg-white/[0.06] dark:hover:bg-white/10",
                    isOutOfCredits ? "border-destructive" : "border-border dark:border-white/25"
                  )}
                  aria-label={bonusCredits > 0
                    ? `AI Credits: ${planRemaining} of ${planLimit} monthly remaining, plus ${bonusCredits} bonus`
                    : `AI Credits: ${planRemaining} of ${planLimit} monthly remaining`}
                >
                  {/* Pulsing red dot for low credits */}
                  {(isLowCredits || isOutOfCredits) && (
                    <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-destructive" />
                    </span>
                  )}
                  <Zap className="h-4 w-4 text-accent" />
                  <div className="flex flex-col items-start">
                    <span className="text-xs font-medium text-foreground">
                      {creditsLoading ? "..." : (
                        <>
                          {/* VA-244: this showed `available` over `planLimit`, and available
                              INCLUDES bonus credits while the limit does not. Any member
                              holding bonus credits therefore read over 100% of their plan —
                              504/500, and 1609/500 on one account. Show plan against plan,
                              and give the bonus its own badge instead of hiding it inside a
                              number it does not belong to. */}
                          {planRemaining}<span className="text-[10px] text-muted-foreground dark:text-foreground/75">/{planLimit}</span>
                          {bonusCredits > 0 && (
                            <span className="ml-1 text-[10px] font-semibold text-primary" title={`${bonusCredits} bonus credits, used after your monthly credits`}>
                              +{bonusCredits}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                    {/* Progress bar  -  shows monthly plan usage only */}
                    <div className="w-14 h-2 rounded-full bg-muted-foreground/20 overflow-hidden">
                      <div
                        className={cn(
                          "h-full transition-all duration-500 rounded-full min-w-[2px]",
                          getProgressColor()
                        )}
                        style={{ width: `${planFillPercent}%` }}
                      />
                    </div>
                  </div>
                </Link>

                {/* Get Credits Button - hidden on very small screens */}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate("/plan")}
                  className="border-brand-pink text-foreground hover:bg-brand-pink/10 hidden sm:flex"
                >
                  <Coins className="h-4 w-4 mr-2" />
                  Get Credits
                </Button>
              </>
            )}

            {/* Hard Refresh Button - PWA only */}
            {isInstalled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleHardRefresh}
                    disabled={refreshing}
                    aria-label="Hard Refresh"
                  >
                    <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-card text-card-foreground border-border">
                  <p className="text-sm">Hard Refresh</p>
                </TooltipContent>
              </Tooltip>
            )}

            {/* User Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 h-10 px-2 hover:bg-muted rounded-lg"
                >
                  <Avatar className="h-8 w-8 border border-border">
                    <AvatarImage src={profile?.avatar_url || undefined} alt={displayName} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden lg:flex flex-col items-start">
                    <span className="text-sm font-medium text-foreground truncate max-w-[120px]">
                      {displayName}
                    </span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-80 bg-brand-deep-purple border-white/10 shadow-lg text-[#f0c5e0] [&_*]:!text-[#f0c5e0]"
                sideOffset={8}
              >
                <DropdownMenuLabel className="flex flex-col gap-0.5 pb-2">
                  <span className="text-sm font-medium">{displayName}</span>
                  <span className="text-xs opacity-70 font-normal truncate">
                    {user?.email}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                {/* Admin account switching. Renders nothing at all for everyone else, and
                    while switched it collapses to a single "Back to <admin>" item at the top
                    of the menu — the way out is the first thing you see. */}
                <ProfileSwitcher />
                <DropdownMenuItem asChild className="focus:bg-white/10">
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-white/10">
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="focus:bg-white/10">
                  <Link to="/plan" className="flex items-center gap-2 cursor-pointer">
                    <CreditCard className="h-4 w-4" />
                    View Credits
                  </Link>
                </DropdownMenuItem>
                {/* Dark mode. A checkbox item rather than a plain one so it announces its
                    state to a screen reader, and onSelect is prevented so the menu stays open
                    — the whole point is watching the page change while you decide. */}
                <DropdownMenuCheckboxItem
                  checked={isDark}
                  onCheckedChange={(next) => setTheme(next ? "dark" : "light")}
                  onSelect={(e) => e.preventDefault()}
                  className="focus:bg-white/10 cursor-pointer"
                >
                  <Moon className="h-4 w-4 mr-2" />
                  Dark mode
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="focus:bg-white/10 cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>

                {/* Which build this browser is actually running.
                    Promised on VA-249 on 18 Aug and never built, and its absence has since
                    cost more than it would have taken. Twice now QA has reported UI missing
                    that is present and wired in the code — the count and the Feature Flags
                    link on VA-239, the retired banner on VA-249 — while the same screens pass
                    automated tests against production in a browser with no service worker.
                    A stale bundle explains all of it, and nobody could confirm or rule it out,
                    so every "it is still broken" was undecidable.
                    Reads VITE_BUILD_TIMESTAMP, which vite.config.ts already injects and
                    useVersionCheck.ts already reads. Nothing new to plumb. */}
                <DropdownMenuSeparator className="bg-white/10" />
                <div className="px-2 py-1.5 text-[11px] leading-tight opacity-60 select-text">
                  Version {buildStamp}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
    </TooltipProvider>
  );
}
